import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, downloadPdf, fmtDate, formatApiError, inr, waLink } from "@/lib/api";
import { Empty, PageHeader, Panel, marginCls } from "@/components/admin/common";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";

export const quoteWhatsappText = (q) =>
  `Hi ${q.customer_name}, here is your Ocean Wings Travel quotation ${q.quote_no} for ${q.package_name}${q.duration ? ` (${q.duration})` : ""}` +
  `${q.travel_date ? `, travelling ${fmtDate(q.travel_date)}` : ""}. Total: ${inr(q.grand_total)} for ${q.adults + q.children} traveller(s). I'm sharing the detailed PDF with you here.`;

export default function Quotes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/quotes").then((r) => setItems(r.data)).finally(() => setLoading(false)); }, []);

  const pdf = async (q) => { try { await downloadPdf(`/admin/quotes/${q.id}/pdf`, `${q.quote_no}.pdf`); } catch (e) { toast.error(formatApiError(e)); } };
  const remove = async (id) => {
    if (!window.confirm("Delete this quotation?")) return;
    await api.delete(`/admin/quotes/${id}`);
    setItems((l) => l.filter((q) => q.id !== id));
    toast.success("Quotation deleted");
  };

  return (
    <div data-testid="admin-quotes-page">
      <PageHeader title="Quotations" subtitle="Saved quotations with cost, selling price and margin. Download branded PDFs anytime.">
        <Button asChild className="bg-ocean hover:bg-navy" data-testid="quotes-new-button"><Link to="/admin/quotes/new"><Plus className="h-4 w-4 mr-2" />New quotation</Link></Button>
      </PageHeader>
      <Panel className="overflow-x-auto">
        {!loading && items.length === 0 ? <Empty testId="quotes-empty">No quotations yet. Create one from an enquiry or start a new quotation.</Empty> : (
          <Table className="min-w-[980px]">
            <TableHeader><TableRow>
              <TableHead>Quote</TableHead><TableHead>Customer</TableHead><TableHead>Package</TableHead><TableHead>Travel</TableHead><TableHead className="text-right">Cost</TableHead><TableHead className="text-right">Customer total</TableHead><TableHead className="text-right">Profit</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((q) => (
                <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                  <TableCell className="font-mono text-xs">{q.quote_no}<p className="text-slate-400">{fmtDate(q.created_at)}</p></TableCell>
                  <TableCell><p className="font-medium text-navy">{q.customer_name}</p><p className="text-xs text-slate-500">{q.phone}</p></TableCell>
                  <TableCell className="max-w-[220px] truncate text-sm">{q.package_name}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{fmtDate(q.travel_date)}<p className="text-xs text-slate-500">{q.adults + q.children} pax</p></TableCell>
                  <TableCell className="text-right font-mono">{inr(q.total_cost)}</TableCell>
                  <TableCell className="text-right font-mono text-ocean">{inr(q.grand_total)}</TableCell>
                  <TableCell className={`text-right font-mono ${marginCls(q.margin)}`}>{inr(q.profit)}<p className="text-xs">{q.margin}%</p></TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      {q.phone && <a href={waLink(q.phone, quoteWhatsappText(q))} target="_blank" rel="noreferrer" data-testid={`quote-whatsapp-${q.id}`} className="h-8 w-8 grid place-items-center rounded-lg text-emerald-600 hover:bg-emerald-50"><WhatsAppIcon className="h-4 w-4" /></a>}
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-ocean" onClick={() => pdf(q)} data-testid={`quote-pdf-${q.id}`}><Download className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => remove(q.id)} data-testid={`quote-delete-${q.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
    </div>
  );
}
